import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useContext, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type {
  ClearanceDetail,
  ClearanceNote,
  PaginatedClearanceNoteList,
  PatchedClearanceNote,
  PatchedClearanceStatusUpdate,
} from '../../../../../js/api';
import { ConnectionsService } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { Button } from '../../../components/button';
import { DrawerCloseButton } from '../../../components/drawer';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Fieldset, Field } from '../../../components/fieldset';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { Textarea } from '../../../components/textarea';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type { ActivityHistory, ActivityStatusHistory } from '../../../utils/activity-types';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ClearanceDetailsTab } from './ClearanceDetailsTab';
import { ClearanceQuickDetails } from './ClearanceQuickDetails';
import { ConnectionsTab } from './ConnectionsTab';
import {
  CLEARANCE_CHANGE_HISTORY_QUERY_KEY,
  CLEARANCE_NOTES_QUERY_KEY,
  CLEARANCE_QUERY_KEY,
  CLEARANCE_STATUS_HISTORY_QUERY_KEY,
  ORGANIZATION_CLEARANCE_LIST_QUERY_KEY,
} from './queryKeys';

const WORKFLOWS_META_QUERY_KEY = 'connections.clearance';

const noteFormSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

type ClearanceDrawerProps = {
  clearanceId: string;
  onClose?: () => void;
  prevId?: string | null;
  nextId?: string | null;
  selectedQueueCode?: string | undefined;
};

export function ClearanceDrawer({
  clearanceId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
}: ClearanceDrawerProps) {
  const queryClient = useQueryClient();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const { user } = useContext(SessionContext);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(
    () => [
      { code: 'details', label: 'Details' as const },
      { code: 'connections', label: 'Connections' as const },
    ],
    []
  );
  const [activeTab, setActiveTab] = useState<string>('connections');

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
    });

  const {
    data: clearanceData,
    isLoading: isLoadingClearance,
    error: clearanceError,
  } = useQuery<ClearanceDetail>({
    queryKey: [CLEARANCE_QUERY_KEY, clearanceId],
    queryFn: () => ConnectionsService.connectionsClearanceRetrieve({ id: clearanceId }),
    enabled: !!clearanceId,
  });

  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  // Helper: after an action refreshes, if this item moved out of the current queue, auto-advance
  const checkAndNavigateIfRemoved = (updated?: ClearanceDetail) => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;

    const currentStatusCode =
      updated?.current_status?.code ?? clearanceData?.current_status?.code ?? null;
    const queueName = getQueueNameForStatus(workflowStatuses as StatusMetadata, currentStatusCode);

    if (queueName && queueName !== selectedQueueCode) {
      const targetId = nextId || prevId || null;
      const nextParams = new URLSearchParams(searchParams);
      if (targetId) {
        nextParams.set('id', String(targetId));
        setSearchParams(nextParams, { replace: true });
      } else {
        nextParams.delete('id');
        setSearchParams(nextParams, { replace: true });
        onClose?.();
      }
    }
  };

  const { data: notesData } = useQuery({
    queryKey: [CLEARANCE_NOTES_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceNotesList({
        id: clearanceId,
        ordering: '-created_at',
      }),
    enabled: !!clearanceId,
    select: (data: PaginatedClearanceNoteList | ClearanceNote) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if ('results' in data) return data.results || [];
      return [data as ClearanceNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [CLEARANCE_STATUS_HISTORY_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceStatusHistoryRetrieve({
        id: clearanceId,
        sort: 'desc',
      }),
    enabled: !!clearanceId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [CLEARANCE_CHANGE_HISTORY_QUERY_KEY, clearanceId],
    queryFn: () =>
      ConnectionsService.connectionsClearanceChangeHistoryRetrieve({
        id: clearanceId,
        sort: 'desc',
      }),
    enabled: !!clearanceId,
  });

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
      next_review_on?: string | null;
    }) => {
      const statusUpdateData: PatchedClearanceStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
        next_review_on: data.next_review_on ?? undefined,
      };
      return ConnectionsService.connectionsClearanceStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedClearance, variables) => {
      const { id } = variables;
      toast.success('Clearance status successfully updated');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CLEARANCE_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_NOTES_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope and global menu badges
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.Clearance],
        }),
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // If item moved out of current queue, navigate to next/prev or close
      checkAndNavigateIfRemoved(updatedClearance as ClearanceDetail);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update clearance status' });
    },
  });

  const noteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: '',
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: NoteFormData }) => {
      return ConnectionsService.connectionsClearanceNotesCreate({
        id,
        requestBody: {
          clearance: id,
          comment: data.comment,
        } as ClearanceNote,
      });
    },
    onSuccess: (_data, variables) => {
      const { id } = variables;
      toast.success('Note added successfully');
      noteForm.reset();
      queryClient.invalidateQueries({ queryKey: [CLEARANCE_NOTES_QUERY_KEY, id] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error adding note' });
    },
  });

  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
    next_review_on?: string | null;
  }) => {
    if (!clearanceId) {
      toast.error('Clearance ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: clearanceId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
      next_review_on: data.next_review_on,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  if (isLoadingClearance) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading clearance details...</div>
      </div>
    );
  }

  if (clearanceError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading clearance details. Please try again.
        </div>
      </div>
    );
  }

  if (!clearanceData) {
    return null;
  }

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={clearanceData?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="connections.clearance"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={clearanceData?.current_reason?.code || null}
        currentStatusTitle={clearanceData?.current_status?.title || null}
      />
      <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section - Provider Info, Status, Close */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
          {/* Provider Avatar - hidden on mobile */}
          <div className="tw:hidden tw:md:flex tw:items-center tw:justify-center tw:size-12 tw:shrink-0 tw:bg-brand-green-1 tw:text-white tw:rounded-full tw:md:order-1">
            <i className="fa-solid fa-user tw:text-lg" />
          </div>
          {/* Provider Name and View Profile link */}
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:order-2">
            <span className="tw:text-base tw:md:text-xl tw:font-bold">
              {clearanceData.provider?.display_name}
            </span>
            {clearanceData.provider?.id && (
              <Link
                to={`/app/healthcare-entity/providers/${clearanceData.provider.id}`}
                className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0 tw:w-fit"
              >
                View Profile
              </Link>
            )}
          </div>
          {/* Status Pill - always below provider info (left-aligned) */}
          <div className="tw:order-4 tw:w-full tw:flex tw:flex-col tw:items-start tw:gap-2">
            <StatusPill scope={WorkflowScopes.Clearance} status={clearanceData.current_status} />
          </div>
          {/* Close Button - right side of row 1 */}
          {onClose && <DrawerCloseButton onClick={onClose} className="tw:order-3" />}
        </div>

        {/* Green Section - DOB, NPI, Specialties */}
        <ClearanceQuickDetails clearance={clearanceData} />
        {user?.permissions.includes('update__connections__clearance') &&
          clearanceData?.reveal_name && (
            <>
              {/* Status Transition Buttons */}
              <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
                <StatusTransitionButtons
                  isLoading={isLoadingClearance}
                  currentStatusCode={clearanceData?.current_status?.code}
                  workflowMetadata={workflowStatuses as StatusMetadata}
                  onStatusClick={(status) => {
                    setPendingTargetStatus(status.code);
                    setIsStatusModalOpen(true);
                  }}
                  isPending={statusUpdateMutation.isPending}
                  small={isMobile}
                />
              </div>
            </>
          )}

        {/* Details and Connections Tabs */}
        <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-4">
          {isOverflowCollapsed ? (
            <div className="tw:pb-2">
              <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
              <DropdownSelect
                inputId="clearance-drawer-section"
                aria-label="Clearance section"
                allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                value={activeTab}
                isSearchable={false}
                isClearable={false}
                onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                  if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                }}
              />
            </div>
          ) : (
            <div ref={tabsMeasureRef} className="tw:w-full">
              <TabsList>
                {tabs.map((t) => (
                  <TabsButton key={t.code} tab={t.code}>
                    {t.label}
                  </TabsButton>
                ))}
              </TabsList>
            </div>
          )}

          <TabsContent tab="details">
            <ClearanceDetailsTab clearance={clearanceData} />
          </TabsContent>
          <TabsContent tab="connections">
            <ConnectionsTab clearance={clearanceData} />
          </TabsContent>
        </Tabs>

        {/* Comments Section */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            noteForm.handleSubmit((data) => createNoteMutation.mutate({ id: clearanceId, data }))(
              e
            );
          }}
          className="tw:mt-4"
        >
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Textarea
                  {...noteForm.register('comment')}
                  placeholder="Enter a comment..."
                  rows={4}
                  disabled={createNoteMutation.isPending}
                />
                {noteForm.formState.errors.comment && (
                  <FieldValidationErrorMessage
                    message={noteForm.formState.errors.comment.message}
                  />
                )}
              </Field>
            </div>
            <div className="tw:flex tw:justify-end tw:mt-4">
              <Button
                type="submit"
                color="brandgreen0"
                disabled={createNoteMutation.isPending}
                small={isMobile}
                onClick={noteForm.handleSubmit((data) =>
                  createNoteMutation.mutate({ id: clearanceId, data })
                )}
              >
                {createNoteMutation.isPending ? 'Adding...' : 'Add Comment'}
              </Button>
            </div>
          </Fieldset>
        </form>

        {/* Activity History */}
        <Tabs defaultTab="activity" className="tw:mt-4">
          <TabsList>
            <TabsButton tab="activity">Activity</TabsButton>
          </TabsList>
          <TabsContent tab="activity" className="tw:pt-0 tw:!mt-0">
            <ActivityLog
              notes={notesData}
              enableCommentsFilter={true}
              enableStatusChangesFilter={clearanceData.reveal_name}
              enableUpdatesFilter={clearanceData.reveal_name}
              enableEventsFilter={false}
              statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
              changeHistory={changeHistory as ActivityHistory[] | undefined}
              eventHistory={undefined}
              noteMutations={{
                updateNote: async (noteId: string, data: PatchedClearanceNote) => {
                  return ConnectionsService.connectionsClearanceNotePartialUpdate({
                    id: noteId,
                    requestBody: data,
                  });
                },
                deleteNote: async (noteId: string) => {
                  return ConnectionsService.connectionsClearanceNoteDestroy({
                    id: noteId,
                  });
                },
                notesQueryKey: [CLEARANCE_NOTES_QUERY_KEY, clearanceId],
                extraInvalidateQueryKeys: [[CLEARANCE_CHANGE_HISTORY_QUERY_KEY, clearanceId]],
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
